import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import CharacterWavingHappily from 'assets/images/characters/CarolinaMariadeJesus/waving-happily.webp';
import EvaluationQRCode from 'assets/images/comparison-letter/evaluation-QRCODE.webp';
import LogoLetrusInverted from 'assets/images/icons/inverted-letrus-logo.webp';
import PageLoading from 'components/PageLoading';
import useComparisonLetter from 'features/useComparisonLetter';
import {Table, Tag} from 'letrus-ui';
import {Modal} from 'components/ui';
import {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {lettersFeedback} from 'utils/functions/involve.me/lettersFeedback';
import loadInvolvePopup from 'utils/functions/involve.me/loadInvolvePopup';
import {useQueryParam} from 'utils/hooks';
import {fetchLetterRequest} from 'APIs/letrusAPI/letters';
import styles from './ComparisonLetter.module.scss';
import EmptyLetter from './components/emptyLetter';

function ComparisonLetter(): JSX.Element {
  const queryParams = useQueryParam();
  const {letterId} = useParams();
  const fetchLetterParams = {is_teacher: queryParams.get('userType')};
  const letterResponse = fetchLetterRequest(letterId, fetchLetterParams);
  const currentLetter = useComparisonLetter(letterResponse?.data);

  const isLetterReady = !!letterResponse?.data;

  useEffect(() => {
    if (isLetterReady) {
      loadInvolvePopup(lettersFeedback, true);
    }
  }, [isLetterReady]);

  return letterResponse.isLoading ? (
    <PageLoading />
  ) : (
    <>
      {isLetterReady ? (
        <>
          {currentLetter.isEmpty ? (
            <EmptyLetter
              schoolName={currentLetter.schoolName}
              studentName={currentLetter.studentName}
              schoolGroupName={currentLetter.schoolGroupName}
            />
          ) : (
            <div className={styles.letterWrapper}>
              <header>
                <div className={styles.letterTitle}>
                  <h1>Boletim de Acompanhamento em Escrita</h1>
                  <Tag
                    text={currentLetter.reviewGrid.toUpperCase()}
                    variant="readOnly"
                    textColor="#2A5082"
                    backgroundColor="#E8F2FC"
                  />
                </div>

                <div className={styles.studentInfo}>
                  <FontAwesomeIcon icon="graduation-cap" scale="1x" />
                  <p>{`${currentLetter.schoolName} - ${currentLetter.studentName} - ${currentLetter.schoolGroupName}`}</p>
                </div>
              </header>

              <main className={styles.letterContent}>
                <section className={styles.banner}>
                  <img src={CharacterWavingHappily} alt="" aria-hidden />

                  <p>
                    Olá! Você está recebendo este boletim com as informações de
                    escrita do estudante, destacando seu desempenho, engajamento
                    ou evolução nas atividades realizadas no Programa Letrus,
                    além de sugestões e comentários para seu desenvolvimento. Na
                    última atividade de redação realizada pelo estudante na
                    Letrus este ano, ele obteve a nota{' '}
                    {currentLetter.totalAverageScore}. Para comparação,{' '}
                    {currentLetter.averageText}. Veja abaixo os detalhes sobre o
                    desempenho do estudante:
                  </p>
                </section>

                <section className={styles.letterSection}>
                  <h2>
                    <FontAwesomeIcon
                      icon={['fad', 'face-thinking']}
                      scale="2.5x"
                    />
                    Como Funciona?
                  </h2>

                  <article className={styles.letterArticle}>
                    <h3>
                      Nota por competência
                      <FontAwesomeIcon icon={['fad', 'dumbbell']} scale="2x" />
                    </h3>

                    <p>{currentLetter?.descriptions?.competenceScore}</p>
                  </article>

                  <article className={styles.letterArticle}>
                    <h3>
                      Comparação de notas
                      <FontAwesomeIcon icon={['fad', 'copy']} scale="2x" />
                    </h3>

                    <p>{currentLetter?.descriptions?.referenceScore}</p>
                  </article>

                  <div className={styles.tableWrapper}>
                    <Table.Container className={styles.scoreTable}>
                      <Table.Header
                        headings={[
                          'Competência',
                          'Nota do Estudante',
                          currentLetter.averageTableHeader,
                          'Diferença',
                        ]}
                      />

                      <tbody>
                        {currentLetter.competences.map((competence) => (
                          <tr key={competence.name}>
                            <td>{competence.name}</td>
                            <td>{competence.studentScore}</td>
                            <td>{competence.referenceScore}</td>
                            <td className={styles[competence.diffColor]}>
                              {competence.scoreDifference}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table.Container>
                  </div>
                </section>

                <section className={styles.letterSection}>
                  <h2>
                    Sugestão e Comentários Gerais
                    <FontAwesomeIcon
                      icon={['fad', 'face-laugh-wink']}
                      scale="2x"
                    />
                  </h2>

                  <div className={styles.tableWrapper}>
                    <Table.Container className={styles.competenceTable}>
                      <Table.Header
                        headings={currentLetter.competences.map(
                          (competence) => competence.name,
                        )}
                      />

                      <tbody>
                        <tr>
                          {currentLetter.competences.map((competence) => (
                            <td key={competence.name}>{competence.detail}</td>
                          ))}
                        </tr>
                        <tr>
                          {currentLetter.competences.map((competence) => (
                            <td key={competence.name}>{competence.comment}</td>
                          ))}
                        </tr>
                      </tbody>
                    </Table.Container>
                  </div>
                </section>
              </main>

              <aside className={styles.evaluationAside}>
                <img
                  src={EvaluationQRCode}
                  alt="QR code para página de avaliação"
                />

                <p>
                  Avalie e nos ajude a melhorar ❤️ Leia o QR CODE ou acesse o
                  link:
                  <br />
                  <a
                    href="https://letrus.pro/cartas"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://letrus.pro/cartas
                  </a>
                </p>
              </aside>

              <footer>
                <img src={LogoLetrusInverted} alt="" aria-hidden />
                <address>
                  <a href="mailto:comunicacao@letrus.com.br">
                    comunicacao@letrus.com.br
                  </a>
                  <br />
                  <a
                    href="https://www.letrus.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    www.letrus.com
                  </a>
                  <br />
                  R. Francisco Leitão, 469 - Pinheiros, São Paulo - SP,
                  05414-901
                </address>
              </footer>
            </div>
          )}
        </>
      ) : (
        <Modal isOpen onClose={() => {}} clickOutsideToClose={false}>
          <div className={styles.modalContent}>
            <h4>Estamos preparando este Boletim!</h4>
            <FontAwesomeIcon
              icon={['fad', 'hand']}
              size="6x"
              color="#F7BB49"
              className="fa-swap-opacity"
            />
            <p>
              Este processo leva alguns minutos. <br />
              Por favor, volte mais tarde.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}

export default ComparisonLetter;
